import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getOrganizations, 
  getOrganizationIntelligence, 
  searchOrganizationWeb, 
  getLatestWebSearch,
  get990TaxData,
  getLatest990Data,
  updateNonprofitInfo,
  updateOrganization,
  autoPopulateOrg,
  applyAutoPopulate
} from '../lib/api';
import { exportRiskOverviewReport } from '../lib/pdfExport';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import OrgSearchPicker from '../components/OrgSearchPicker';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Search,
  AlertTriangle,
  Building2,
  Users,
  UserCheck,
  Shield,
  FileText,
  DollarSign,
  TrendingUp,
  Globe,
  Newspaper,
  Scale,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Loader2,
  Info,
  XCircle,
  BarChart3,
  PieChart,
  Calendar,
  Lightbulb,
  ChevronRight,
  Settings,
  Save,
  Download,
  Award,
  ClipboardCheck,
  Wand2,
  Check,
  X,
  ChevronDown
} from 'lucide-react';

const SEVERITY_CONFIG = {
  low: { color: 'bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30', icon: Info },
  medium: { color: 'bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/30', icon: AlertTriangle },
  high: { color: 'bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30', icon: AlertCircle },
  info: { color: 'bg-primary/20 text-primary border-primary/30', icon: Info },
};

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'bg-[#FF9500]/20 text-[#FF9500]' },
  clear: { icon: CheckCircle, color: 'bg-[#34C759]/20 text-[#34C759]' },
  flagged: { icon: XCircle, color: 'bg-[#FF3B30]/20 text-[#FF3B30]' },
};

export default function WebMonitoring() {
  const navigate = useNavigate();
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [localOrgId, setLocalOrgId] = useState(''); // Only used for non-super-admin org selector
  const [intelligence, setIntelligence] = useState(null);
  const [webSearchResults, setWebSearchResults] = useState(null);
  const [newsFilter, setNewsFilter] = useState('all'); // 'all', 'risk_alerts', 'legal_filings'
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [loadingTax, setLoadingTax] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEinDialog, setShowEinDialog] = useState(false);
  const [einInput, setEinInput] = useState('');
  const [isNonprofit, setIsNonprofit] = useState(false);
  // GL Effective Date state
  const [glRenewalDate, setGlRenewalDate] = useState('');
  const [savingGlDate, setSavingGlDate] = useState(false);
  // Custom risk keywords state
  const [customKeywords, setCustomKeywords] = useState(['', '', '', '', '']);
  const [showKeywordsDialog, setShowKeywordsDialog] = useState(false);
  const [savingKeywords, setSavingKeywords] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [showAutoPopulatePreview, setShowAutoPopulatePreview] = useState(false);
  const [autoPopulateData, setAutoPopulateData] = useState(null);
  const [selectedChanges, setSelectedChanges] = useState({});
  const [applyingChanges, setApplyingChanges] = useState(false);
  
  const { isSuperAdmin } = useAuth();
  const { isPlatformMode, isOrganizationMode, selectedOrg } = useAdminMode();

  // Read org ID from URL query parameter (for drill-down from Network Intelligence)
  const urlParams = new URLSearchParams(location.search);
  const urlOrgId = urlParams.get('org');

  // Derive the effective org ID: URL param > local selector > Super Admin context
  const effectiveOrgId = urlOrgId || localOrgId || (isSuperAdmin() ? selectedOrg?.id : null);
  
  // Handle org selection - update URL to enable bookmarking/sharing
  const handleOrgSelect = (orgId) => {
    setLocalOrgId(orgId);
    // Update URL without full page reload
    navigate(`/org-iq?org=${orgId}`, { replace: true });
  };

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await getOrganizations();
      const orgs = response.data || [];
      
      // Sort organizations hierarchically: National > Regional > State > Local
      const levelOrder = {
        'national': 0,
        'regional': 1,
        'state': 2,
        'local': 3,
        'franchise': 4,
        'franchisee': 5
      };
      
      const sortedOrgs = orgs.sort((a, b) => {
        const levelA = levelOrder[a.level] ?? 99;
        const levelB = levelOrder[b.level] ?? 99;
        if (levelA !== levelB) return levelA - levelB;
        return a.name.localeCompare(b.name);
      });
      
      setOrganizations(sortedOrgs);
      
      // If URL has an org param, use that
      if (urlOrgId && sortedOrgs.some(o => o.id === urlOrgId)) {
        setLocalOrgId(urlOrgId);
      }
      // For non-super-admins, default to their own org (National) first
      else if (!isSuperAdmin() && sortedOrgs.length > 0) {
        // Find the National org (should be first after sorting)
        const nationalOrg = sortedOrgs.find(o => o.level === 'national');
        setLocalOrgId(nationalOrg?.id || sortedOrgs[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, urlOrgId]);

  const fetchIntelligence = useCallback(async (orgId) => {
    if (!orgId) return;
    
    try {
      const response = await getOrganizationIntelligence(orgId);
      setIntelligence(response.data);
      setIsNonprofit(response.data.organization?.is_nonprofit || false);
      setEinInput(response.data.organization?.ein || '');
      // Set GL Effective Date from org data
      setGlRenewalDate(response.data.organization?.gl_policy_renewal_date || '');
      // Set custom risk keywords from org data
      const keywords = response.data.organization?.custom_risk_keywords || [];
      setCustomKeywords([...keywords, ...Array(5 - keywords.length).fill('')].slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch intelligence:', error);
    }
  }, []);

  const fetchLatestWebSearch = useCallback(async (orgId) => {
    if (!orgId) return;
    
    try {
      const response = await getLatestWebSearch(orgId);
      if (response.data) {
        setWebSearchResults(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch latest web search:', error);
    }
  }, []);

  const fetchLatest990Data = useCallback(async (orgId) => {
    if (!orgId) return;
    
    try {
      const response = await getLatest990Data(orgId);
      if (response.data) {
        setTaxData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch latest 990 data:', error);
    }
  }, []);

  useEffect(() => {
    // In Platform Mode, don't load data - show message instead
    if (isSuperAdmin() && isPlatformMode) {
      setLoading(false);
      return;
    }
    fetchOrganizations();
  }, [fetchOrganizations, isSuperAdmin, isPlatformMode]);

  // Clear data when org changes to force fresh fetch
  useEffect(() => {
    if (effectiveOrgId) {
      // Reset state to show loading for new org
      setIntelligence(null);
      setWebSearchResults(null);
      setTaxData(null);
      // Fetch new data
      fetchIntelligence(effectiveOrgId);
      fetchLatestWebSearch(effectiveOrgId);
      fetchLatest990Data(effectiveOrgId);
    }
  }, [effectiveOrgId, fetchIntelligence, fetchLatestWebSearch, fetchLatest990Data]);

  // Super Admin Platform Mode - Show message to switch to Organization Mode
  // BUT allow viewing if URL has an org parameter (drill-down from dashboard)
  if (isSuperAdmin() && isPlatformMode && !urlOrgId) {
    return (
      <div data-testid="org-iq-page" className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Risk Overview
          </h1>
          <p className="text-muted-foreground mt-1">
            Risk Overview & Risk Monitoring
          </p>
        </div>
        
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Globe className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-600">
            <strong>Organization View Required:</strong> Risk Overview provides deep intelligence for individual organizations. 
            Switch to Organization Mode and select an organization to view their intelligence dashboard, web monitoring, and 990 tax data.
          </AlertDescription>
        </Alert>
        
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Switch to Organization View</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Risk Overview is designed for organization-level deep dives. Use the mode toggle in the admin bar above to switch to Organization View, then select an organization to see their complete intelligence profile.
          </p>
        </Card>
      </div>
    );
  }

  // Super Admin Organization Mode - No org selected (and no URL param)
  if (isSuperAdmin() && isOrganizationMode && !selectedOrg && !urlOrgId) {
    return (
      <div data-testid="org-iq-page" className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Risk Overview
          </h1>
          <p className="text-muted-foreground mt-1">Select an organization to view intelligence</p>
        </div>
        
        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <AlertDescription className="text-yellow-600">
            Please select an organization from the admin bar above to view their intelligence dashboard.
          </AlertDescription>
        </Alert>
        
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Organization Selected</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Use the organization selector in the admin bar to choose an organization and view their intelligence profile.
          </p>
        </Card>
      </div>
    );
  }

  // Loading state for regular users
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Organization context banner for super admins
  const OrgContextBanner = () => {
    if (!isSuperAdmin() || !isOrganizationMode || !selectedOrg) return null;
    return (
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-4">
        <Building2 className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          Viewing intelligence for <strong>{selectedOrg.name}</strong>
        </AlertDescription>
      </Alert>
    );
  };

  const handleWebSearch = async () => {
    if (!effectiveOrgId) return;
    
    setSearching(true);
    try {
      const response = await searchOrganizationWeb(effectiveOrgId);
      setWebSearchResults(response.data);
      toast.success('Web search completed');
      // Refresh intelligence to get new alerts
      fetchIntelligence(effectiveOrgId);
    } catch (error) {
      toast.error('Web search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleFetch990Data = async () => {
    if (!effectiveOrgId) return;
    
    setLoadingTax(true);
    try {
      const response = await get990TaxData(effectiveOrgId);
      setTaxData(response.data);
      if (response.data.tax_records?.length > 0) {
        toast.success('990 tax data retrieved');
      } else if (response.data.potential_matches?.length > 0) {
        toast.info('Found potential nonprofit matches');
      } else {
        toast.info('No 990 data found for this organization');
      }
    } catch (error) {
      toast.error('Failed to fetch 990 data');
    } finally {
      setLoadingTax(false);
    }
  };

  const handleSaveCustomKeywords = async () => {
    if (!effectiveOrgId) return;
    
    setSavingKeywords(true);
    try {
      // Filter out empty keywords
      const keywords = customKeywords.filter(k => k.trim() !== '');
      await updateOrganization(effectiveOrgId, { custom_risk_keywords: keywords });
      toast.success('Custom risk keywords saved');
      setShowKeywordsDialog(false);
    } catch (error) {
      toast.error('Failed to save custom keywords');
    } finally {
      setSavingKeywords(false);
    }
  };

  const updateKeyword = (index, value) => {
    const newKeywords = [...customKeywords];
    newKeywords[index] = value;
    setCustomKeywords(newKeywords);
  };

  const handleUpdateNonprofitInfo = async () => {
    try {
      await updateNonprofitInfo(effectiveOrgId, {
        ein: einInput,
        is_nonprofit: isNonprofit
      });
      toast.success('Nonprofit information updated');
      setShowEinDialog(false);
      fetchIntelligence(effectiveOrgId);
    } catch (error) {
      toast.error('Failed to update nonprofit info');
    }
  };

  const handleSaveGlRenewalDate = async () => {
    if (!effectiveOrgId) return;
    
    setSavingGlDate(true);
    try {
      await updateOrganization(effectiveOrgId, {
        gl_policy_renewal_date: glRenewalDate || null
      });
      toast.success('GL Effective Date updated');
      fetchIntelligence(effectiveOrgId);
    } catch (error) {
      toast.error('Failed to update GL Effective Date');
    } finally {
      setSavingGlDate(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  const org = intelligence?.organization;
  const staffMembers = intelligence?.staff || [];
  const boardMembers = intelligence?.board_members || [];
  const alerts = intelligence?.alerts || [];
  const claimsSummary = intelligence?.claims_summary || {};
  const incidentsSummary = intelligence?.incidents_summary || {};
  const assessments = intelligence?.assessments || [];

  const handleAutoPopulate = async () => {
    if (!effectiveOrgId) return;
    setAutoPopulating(true);
    try {
      const response = await autoPopulateOrg(effectiveOrgId);
      const preview = response.data;
      setAutoPopulateData(preview);
      // Initialize all discovered items as selected
      const initial = {};
      if (preview.discovered?.mission_statement) initial.mission_statement = true;
      if (preview.discovered?.contact_info) initial.contact_info = true;
      if (preview.discovered?.social_media) initial.social_media = true;
      if (preview.discovered?.org_size) initial.org_size = true;
      if (preview.discovered?.ein && !preview.existing_data?.ein) initial.ein = true;
      if (preview.discovered?.is_nonprofit) initial.is_nonprofit = true;
      if (preview.discovered?.leadership?.length) {
        initial.leadership = preview.discovered.leadership.filter(p => p.is_new).map((_, i) => i);
      }
      if (preview.discovered?.staff_members?.length) {
        initial.staff_members = preview.discovered.staff_members.filter(p => p.is_new).map((_, i) => i);
      }
      if (preview.discovered?.board_members?.length) {
        initial.board_members = preview.discovered.board_members.filter(p => p.is_new).map((_, i) => i);
      }
      setSelectedChanges(initial);
      setShowAutoPopulatePreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch organization data from web sources');
    } finally {
      setAutoPopulating(false);
    }
  };

  const toggleChange = (key) => {
    setSelectedChanges(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePersonIndex = (key, idx) => {
    setSelectedChanges(prev => {
      const current = prev[key] || [];
      const next = current.includes(idx) ? current.filter(i => i !== idx) : [...current, idx];
      return { ...prev, [key]: next };
    });
  };

  const handleApplyChanges = async () => {
    if (!autoPopulateData) return;
    setApplyingChanges(true);
    try {
      const d = autoPopulateData.discovered;
      const payload = {};
      if (selectedChanges.mission_statement && d.mission_statement) payload.mission_statement = d.mission_statement;
      if (selectedChanges.contact_info && d.contact_info) payload.contact_info = d.contact_info;
      if (selectedChanges.social_media && d.social_media) payload.social_media = d.social_media;
      if (selectedChanges.org_size && d.org_size) payload.org_size = d.org_size;
      if (selectedChanges.ein && d.ein) payload.ein = d.ein;
      if (selectedChanges.is_nonprofit) payload.is_nonprofit = d.is_nonprofit;
      if (selectedChanges.leadership?.length && d.leadership) {
        payload.leadership = selectedChanges.leadership.map(i => d.leadership.filter(p => p.is_new)[i]).filter(Boolean);
      }
      if (selectedChanges.staff_members?.length && d.staff_members) {
        payload.staff = selectedChanges.staff_members.map(i => d.staff_members.filter(p => p.is_new)[i]).filter(Boolean);
      }
      if (selectedChanges.board_members?.length && d.board_members) {
        payload.board_members = selectedChanges.board_members.map(i => d.board_members.filter(p => p.is_new)[i]).filter(Boolean);
      }

      await applyAutoPopulate(effectiveOrgId, payload);
      toast.success('Organization profile updated successfully');
      setShowAutoPopulatePreview(false);
      setAutoPopulateData(null);
      fetchIntelligence(effectiveOrgId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply changes');
    } finally {
      setApplyingChanges(false);
    }
  };

  return (
    <div data-testid="org-iq-page" className="space-y-6 animate-fade-in">
      {/* Org Context Banner */}
      <OrgContextBanner />
      

      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            {intelligence?.organization?.name ? `${intelligence.organization.name} - Risk Overview` : 'Risk Overview'}
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-1">
            Comprehensive view of organization data, web presence, and risk monitoring
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Export Button */}
          {intelligence && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportRiskOverviewReport(intelligence, intelligence?.organization?.name)}
              data-testid="export-risk-overview-btn"
              className="text-xs sm:text-sm h-8 sm:h-9"
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              <span className="hidden sm:inline">Export PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
          )}
          {/* Smart Org Search Picker for non-super-admin */}
          {!isSuperAdmin() && organizations.length > 0 && (
            <OrgSearchPicker
              organizations={organizations}
              selectedOrgId={effectiveOrgId}
              onSelect={handleOrgSelect}
              placeholder="Search organizations..."
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50 h-auto flex-wrap">
          <TabsTrigger value="overview" className="text-xs sm:text-sm py-1.5 sm:py-2">Overview</TabsTrigger>
          <TabsTrigger value="people" className="text-xs sm:text-sm py-1.5 sm:py-2">People</TabsTrigger>
          <TabsTrigger value="web" className="text-xs sm:text-sm py-1.5 sm:py-2">
            <span className="hidden sm:inline">Web & News</span>
            <span className="sm:hidden">Web</span>
          </TabsTrigger>
          {(isNonprofit || org?.is_nonprofit) && (
            <TabsTrigger value="990" className="text-xs sm:text-sm py-1.5 sm:py-2">990 Data</TabsTrigger>
          )}
          <TabsTrigger value="alerts" className="text-xs sm:text-sm py-1.5 sm:py-2">Alerts ({alerts.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          {/* Organization Card */}
          <Card className="bg-card border-border">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-background flex items-center justify-center overflow-hidden border border-border flex-shrink-0">
                    {org?.logo_url ? (
                      <>
                        <img 
                          src={org.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}` : org.logo_url}
                          alt={`${org.name} logo`}
                          className="w-full h-full object-contain"
                          data-testid="org-header-logo"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div className="w-full h-full items-center justify-center hidden">
                          <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                        </div>
                      </>
                    ) : (
                      <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <CardTitle className="text-lg sm:text-xl truncate">{org?.name || 'Select an Organization'}</CardTitle>
                    <CardDescription className="flex flex-wrap gap-1.5 mt-1">
                      {org?.level && <Badge variant="outline" className="capitalize text-[10px] sm:text-xs">{org.level}</Badge>}
                      {org?.is_nonprofit && <Badge className="bg-green-500/20 text-green-500 text-[10px] sm:text-xs">501(c) Nonprofit</Badge>}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-2 self-start sm:self-center">
                  {org?.org_type !== 'fitness' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleAutoPopulate}
                      disabled={autoPopulating}
                      data-testid="auto-populate-btn"
                      className="text-xs sm:text-sm h-8 sm:h-9"
                    >
                      {autoPopulating ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {autoPopulating ? 'Scanning...' : 'Auto-Populate'}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowEinDialog(true)} className="text-xs sm:text-sm h-8 sm:h-9 self-start sm:self-center">
                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    <span className="hidden sm:inline">Edit Nonprofit Info</span>
                    <span className="sm:hidden">Edit Info</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="p-3 sm:p-4 rounded-lg bg-secondary/30">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">EIN</p>
                  <p className="font-semibold text-sm sm:text-base truncate">{org?.ein || 'Not Set'}</p>
                </div>
                <div className="p-3 sm:p-4 rounded-lg bg-secondary/30">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Org Level</p>
                  <p className="font-semibold text-sm sm:text-base capitalize">{org?.level || 'N/A'}</p>
                </div>
                <div className="p-3 sm:p-4 rounded-lg bg-secondary/30">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Staff</p>
                  <p className="font-semibold text-sm sm:text-base">{staffMembers.length}</p>
                </div>
                <div className="p-3 sm:p-4 rounded-lg bg-secondary/30">
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase">Board</p>
                  <p className="font-semibold">{boardMembers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insurance & Renewal Information Card */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Insurance & Renewal</CardTitle>
                    <CardDescription>GL effective date determines assessment renewal window</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gl-renewal-date" className="text-sm font-medium">
                    GL Effective Date
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="gl-renewal-date"
                      type="date"
                      value={glRenewalDate ? glRenewalDate.split('T')[0] : ''}
                      onChange={(e) => setGlRenewalDate(e.target.value)}
                      className="flex-1"
                      data-testid="gl-renewal-date-input"
                    />
                    <Button 
                      onClick={handleSaveGlRenewalDate}
                      disabled={savingGlDate}
                      size="sm"
                      data-testid="save-gl-date-btn"
                    >
                      {savingGlDate ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Assessment renewal window opens 60 days before the 1-year anniversary
                  </p>
                </div>
                
                {glRenewalDate && (
                  <>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <p className="text-xs text-amber-600 uppercase font-medium mb-1">Renewal Window Opens</p>
                      <p className="font-semibold">
                        {(() => {
                          // Calculate next anniversary (1 year from effective date)
                          const effectiveDate = new Date(glRenewalDate);
                          const today = new Date();
                          let nextAnniversary = new Date(effectiveDate);
                          nextAnniversary.setFullYear(today.getFullYear());
                          
                          // If this year's anniversary has passed, use next year's
                          if (nextAnniversary <= today) {
                            nextAnniversary.setFullYear(today.getFullYear() + 1);
                          }
                          
                          // Renewal window opens 60 days before the anniversary
                          const windowOpens = new Date(nextAnniversary);
                          windowOpens.setDate(windowOpens.getDate() - 60);
                          return windowOpens.toLocaleDateString();
                        })()}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">60 days before anniversary</p>
                    </div>
                    <div className="p-3 rounded-lg bg-secondary/30">
                      <p className="text-xs text-muted-foreground uppercase mb-1">Policy Expiration Date</p>
                      <p className="font-semibold">
                        {(() => {
                          const effectiveDate = new Date(glRenewalDate);
                          const today = new Date();
                          let nextAnniversary = new Date(effectiveDate);
                          nextAnniversary.setFullYear(today.getFullYear());
                          
                          // If this year's anniversary has passed, use next year's
                          if (nextAnniversary <= today) {
                            nextAnniversary.setFullYear(today.getFullYear() + 1);
                          }
                          
                          const days = Math.ceil((nextAnniversary - today) / (1000 * 60 * 60 * 24));
                          return `${nextAnniversary.toLocaleDateString()} (${days} days)`;
                        })()}
                      </p>
                    </div>
                  </>
                )}
                
                {!glRenewalDate && (
                  <div className="col-span-2 p-4 rounded-lg bg-secondary/20 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="text-sm font-medium">No GL Effective Date Set</p>
                      <p className="text-xs text-muted-foreground">
                        Set the GL effective date to align assessment renewals with your insurance cycle
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card className={`metric-card ${
              assessments[0]?.risk_level === 'critical' ? 'border-[#FF3B30]/50' :
              assessments[0]?.risk_level === 'high' ? 'border-[#FF9500]/50' :
              assessments[0]?.risk_level === 'medium' ? 'border-[#FFCC00]/50' :
              assessments[0]?.risk_level === 'low' ? 'border-[#34C759]/50' : ''
            }`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase">Safety Score</p>
                    <p className={`text-2xl sm:text-4xl font-bold font-['Barlow_Condensed'] mt-1 ${
                      assessments[0]?.risk_level === 'critical' ? 'text-[#FF3B30]' :
                      assessments[0]?.risk_level === 'high' ? 'text-[#FF9500]' :
                      assessments[0]?.risk_level === 'medium' ? 'text-[#FFCC00]' :
                      assessments[0]?.risk_level === 'low' ? 'text-[#34C759]' : ''
                    }`}>
                      {assessments[0]?.score != null ? `${assessments[0].score}%` : '--'}
                    </p>
                    {assessments[0]?.risk_level && (
                      <Badge className={`mt-1 uppercase text-[10px] sm:text-xs ${
                        assessments[0].risk_level === 'critical' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                        assessments[0].risk_level === 'high' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                        assessments[0].risk_level === 'medium' ? 'bg-[#FFCC00]/20 text-[#FFCC00]' :
                        'bg-[#34C759]/20 text-[#34C759]'
                      }`}>
                        {assessments[0].risk_level} Risk
                      </Badge>
                    )}
                  </div>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    assessments[0]?.risk_level === 'critical' ? 'bg-[#FF3B30]/10' :
                    assessments[0]?.risk_level === 'high' ? 'bg-[#FF9500]/10' :
                    assessments[0]?.risk_level === 'medium' ? 'bg-[#FFCC00]/10' :
                    assessments[0]?.risk_level === 'low' ? 'bg-[#34C759]/10' : 'bg-primary/10'
                  }`}>
                    <Shield className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      assessments[0]?.risk_level === 'critical' ? 'text-[#FF3B30]' :
                      assessments[0]?.risk_level === 'high' ? 'text-[#FF9500]' :
                      assessments[0]?.risk_level === 'medium' ? 'text-[#FFCC00]' :
                      assessments[0]?.risk_level === 'low' ? 'text-[#34C759]' : 'text-primary'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase">Total Incidents</p>
                    <p className="text-2xl sm:text-4xl font-bold font-['Barlow_Condensed'] mt-1">
                      {incidentsSummary.total_incidents || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-[#FF9500]/10 flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF9500]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase">Open Incidents</p>
                    <p className="text-2xl sm:text-4xl font-bold font-['Barlow_Condensed'] mt-1 text-[#FF3B30]">
                      {incidentsSummary.open_incidents || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Requiring attention</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-[#FF3B30]/10 flex-shrink-0">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF3B30]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="metric-card">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase">Active Alerts</p>
                    <p className="text-2xl sm:text-4xl font-bold font-['Barlow_Condensed'] mt-1">
                      {alerts.filter(a => !a.is_read).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-[#FFCC00]/10 flex-shrink-0">
                    <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFCC00]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Professional Sports Assessment Section - Only for professional_sports orgs */}
          {org?.org_type === 'professional_sports' && (
            <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                      <Award className="w-5 h-5 text-purple-500" />
                      Professional Sports Operations Assessment
                    </CardTitle>
                    <CardDescription>
                      80-question enterprise risk assessment across Facilities, Events, and Cyber
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => navigate(`/assessments/professional-sports/${effectiveOrgId}`)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    {org?.latest_assessment_id ? 'View / Retake Assessment' : 'Start Assessment'}
                  </Button>
                </div>
              </CardHeader>
              {org?.latest_assessment_id && (
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 rounded-lg bg-background/50" data-testid="pro-sports-enterprise-score">
                      <div className="text-2xl font-bold text-purple-500">
                        {org?.latest_assessment_score?.toFixed(0) || '--'}
                      </div>
                      <div className="text-xs text-muted-foreground">Enterprise Score</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background/50" data-testid="pro-sports-risk-band">
                      <Badge className={`
                        ${org?.latest_assessment_risk_band === 'Elite' ? 'bg-emerald-500' :
                          org?.latest_assessment_risk_band === 'Strong' ? 'bg-green-500' :
                          org?.latest_assessment_risk_band === 'Moderate' ? 'bg-yellow-500' :
                          org?.latest_assessment_risk_band === 'Elevated' ? 'bg-orange-500' :
                          'bg-red-500'} text-white
                      `}>
                        {org?.latest_assessment_risk_band || '--'}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">Risk Band</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background/50" data-testid="pro-sports-critical-flags">
                      <div className={`text-lg font-bold ${org?.has_critical_flags ? 'text-red-500' : 'text-green-500'}`}>
                        {org?.has_critical_flags ? 'Yes' : 'No'}
                      </div>
                      <div className="text-xs text-muted-foreground">Critical Flags</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background/50" data-testid="pro-sports-last-assessed">
                      <div className="text-sm font-medium">
                        {org?.latest_assessment_date ? new Date(org.latest_assessment_date).toLocaleDateString() : '--'}
                      </div>
                      <div className="text-xs text-muted-foreground">Last Assessed</div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Quick Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Incident Status */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Incident Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">New</span>
                    <div className="flex items-center gap-2">
                      <Progress value={(incidentsSummary.by_status?.new / incidentsSummary.total_incidents) * 100 || 0} className="w-24 h-2" />
                      <span className="text-sm font-semibold w-8">{incidentsSummary.by_status?.new || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Open</span>
                    <div className="flex items-center gap-2">
                      <Progress value={(incidentsSummary.by_status?.open / incidentsSummary.total_incidents) * 100 || 0} className="w-24 h-2" />
                      <span className="text-sm font-semibold w-8">{incidentsSummary.by_status?.open || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Investigating</span>
                    <div className="flex items-center gap-2">
                      <Progress value={(incidentsSummary.by_status?.investigating / incidentsSummary.total_incidents) * 100 || 0} className="w-24 h-2" />
                      <span className="text-sm font-semibold w-8">{incidentsSummary.by_status?.investigating || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Resolved</span>
                    <div className="flex items-center gap-2">
                      <Progress value={(incidentsSummary.by_status?.resolved / incidentsSummary.total_incidents) * 100 || 0} className="w-24 h-2" />
                      <span className="text-sm font-semibold w-8">{incidentsSummary.by_status?.resolved || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Closed</span>
                    <div className="flex items-center gap-2">
                      <Progress value={(incidentsSummary.by_status?.closed / incidentsSummary.total_incidents) * 100 || 0} className="w-24 h-2" />
                      <span className="text-sm font-semibold w-8">{incidentsSummary.by_status?.closed || 0}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Claims Summary - Hidden: Not launching with claims functionality */}
          </div>

          {/* Assessment Recommendations Section */}
          {assessments[0]?.recommendations?.length > 0 && (
            <Card className="bg-card border-border border-l-4 border-l-yellow-500">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  Safety Recommendations
                </CardTitle>
                <CardDescription>
                  Based on your latest safety assessment ({assessments[0]?.assessment_type || 'General'}) - Score: {assessments[0]?.score}%
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assessments[0].recommendations.slice(0, 8).map((rec, idx) => (
                    <div 
                      key={`rec-${rec?.id || rec?.title || rec || idx}`} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 hover:bg-yellow-500/10 transition-colors"
                    >
                      <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-yellow-600">{idx + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">{rec}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  ))}
                  {assessments[0].recommendations.length > 8 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      +{assessments[0].recommendations.length - 8} more recommendations available in the full assessment
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Assessment Prompt */}
          {!assessments[0] && (
            <Card className="bg-card border-border border-dashed">
              <CardContent className="py-8 text-center">
                <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No Safety Assessment</h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                  Complete a safety assessment to receive personalized recommendations for improving your organization's risk posture.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/assessments'}
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  Start Assessment
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* People Tab */}
        <TabsContent value="people" className="space-y-6">
          {/* Staff Members */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Staff Members ({staffMembers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {staffMembers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Background Check</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffMembers.map((member) => {
                      const statusConfig = STATUS_CONFIG[member.background_check_status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={member.id} className="border-border">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                                <span className="text-xs font-medium">
                                  {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{member.name}</p>
                                {member.date_of_birth && (
                                  <p className="text-xs text-muted-foreground">DOB: {member.date_of_birth}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{member.role}</TableCell>
                          <TableCell className="text-muted-foreground">{member.email || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig.color} uppercase text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {member.background_check_status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No staff members found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Board Members */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" />
                Board Members ({boardMembers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {boardMembers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Background Check</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {boardMembers.map((member) => {
                      const statusConfig = STATUS_CONFIG[member.background_check_status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={member.id} className="border-border">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#9B59B6]/20 flex items-center justify-center">
                                <span className="text-xs font-medium text-[#9B59B6]">
                                  {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{member.name}</p>
                                {member.date_of_birth && (
                                  <p className="text-xs text-muted-foreground">DOB: {member.date_of_birth}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{member.role}</TableCell>
                          <TableCell className="text-muted-foreground">{member.email || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig.color} uppercase text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {member.background_check_status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No board members found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Web & News Tab */}
        <TabsContent value="web" className="space-y-6">
          {/* Search Info */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-start gap-4">
              <Globe className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold">Web & News Monitoring</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Automatically scans every 24 hours for adverse media, public records, arrests, lawsuits, and misconduct allegations 
                  related to your organization and personnel. Results are analyzed for risk indicators.
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <Dialog open={showKeywordsDialog} onOpenChange={setShowKeywordsDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings className="w-4 h-4 mr-2" />
                        Custom Keywords
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border">
                      <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                          Custom Risk Keywords
                        </DialogTitle>
                        <DialogDescription>
                          Add up to 5 custom keywords or names to monitor. These will be flagged as risk alerts when found in news articles.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 mt-4">
                        {customKeywords.map((keyword, index) => (
                          <div key={`kw-slot-${index}`} className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                            <Input
                              value={keyword}
                              onChange={(e) => updateKeyword(index, e.target.value)}
                              placeholder={`Keyword or name ${index + 1}`}
                              className="bg-secondary/50"
                            />
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground mt-2">
                          Examples: competitor name, former employee, specific incident type
                        </p>
                      </div>
                      <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setShowKeywordsDialog(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveCustomKeywords} disabled={savingKeywords}>
                          {savingKeywords ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Keywords
                        </Button>
                        <Button 
                          onClick={async () => {
                            await handleSaveCustomKeywords();
                            setShowKeywordsDialog(false);
                            handleWebSearch();
                          }} 
                          disabled={savingKeywords || searching}
                          variant="default"
                        >
                          {searching ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Save & Scan Now
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button
                    onClick={handleWebSearch}
                    disabled={searching || !effectiveOrgId}
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                  >
                    {searching ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Scan Now
                  </Button>
                </div>
                {/* Show active custom keywords */}
                {customKeywords.some(k => k.trim()) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs text-muted-foreground">Monitoring:</span>
                    {customKeywords.filter(k => k.trim()).map((keyword) => (
                      <Badge key={`kw-badge-${keyword}`} variant="secondary" className="text-xs">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {webSearchResults ? (
            <>
              {/* Search Summary */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                    <Search className="w-5 h-5 text-primary" />
                    Search Results
                  </CardTitle>
                  <CardDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span>
                      Searched: "{webSearchResults.organization_name}" on {new Date(webSearchResults.search_date).toLocaleString()}
                    </span>
                    <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full inline-flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" />
                      Auto-scans daily at 3:00 AM UTC
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                      onClick={() => setNewsFilter(newsFilter === 'all' ? 'all' : 'all')}
                      className={`p-4 rounded-lg text-center transition-all cursor-pointer ${
                        newsFilter === 'all' 
                          ? 'bg-primary/20 ring-2 ring-primary' 
                          : 'bg-secondary/30 hover:bg-secondary/50'
                      }`}
                    >
                      <Newspaper className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{webSearchResults.news_mentions?.length || 0}</p>
                      <p className="text-sm text-muted-foreground">All News</p>
                    </button>
                    <button 
                      onClick={() => setNewsFilter(newsFilter === 'risk_alerts' ? 'all' : 'risk_alerts')}
                      className={`p-4 rounded-lg text-center transition-all cursor-pointer ${
                        newsFilter === 'risk_alerts' 
                          ? 'bg-[#FF9500]/20 ring-2 ring-[#FF9500]' 
                          : 'bg-secondary/30 hover:bg-secondary/50'
                      }`}
                    >
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-[#FF9500]" />
                      <p className="text-2xl font-bold">{webSearchResults.risk_alerts?.length || 0}</p>
                      <p className="text-sm text-muted-foreground">Risk Alerts</p>
                      {newsFilter !== 'risk_alerts' && webSearchResults.risk_alerts?.length > 0 && (
                        <p className="text-xs text-[#FF9500] mt-1">Click to filter</p>
                      )}
                    </button>
                    <button 
                      onClick={() => setNewsFilter(newsFilter === 'legal_filings' ? 'all' : 'legal_filings')}
                      className={`p-4 rounded-lg text-center transition-all cursor-pointer ${
                        newsFilter === 'legal_filings' 
                          ? 'bg-[#FF3B30]/20 ring-2 ring-[#FF3B30]' 
                          : 'bg-secondary/30 hover:bg-secondary/50'
                      }`}
                    >
                      <Scale className="w-8 h-8 mx-auto mb-2 text-[#FF3B30]" />
                      <p className="text-2xl font-bold">{webSearchResults.legal_filings?.length || 0}</p>
                      <p className="text-sm text-muted-foreground">Legal Filings</p>
                      {newsFilter !== 'legal_filings' && webSearchResults.legal_filings?.length > 0 && (
                        <p className="text-xs text-[#FF3B30] mt-1">Click to filter</p>
                      )}
                    </button>
                  </div>
                  {newsFilter !== 'all' && (
                    <div className="mt-4 flex items-center justify-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setNewsFilter('all')}
                        className="text-muted-foreground"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Clear Filter
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* News Mentions - Filtered based on selected filter */}
              {(() => {
                // Get the articles to display based on the filter
                let articlesToShow = [];
                let sectionTitle = "News Mentions";
                let sectionIcon = <Newspaper className="w-5 h-5 text-primary" />;
                
                if (newsFilter === 'risk_alerts') {
                  // Show risk alerts directly as articles
                  if (webSearchResults.risk_alerts?.length > 0) {
                    articlesToShow = webSearchResults.risk_alerts.map(alert => ({
                      title: alert.title || `Risk Alert: ${alert.keyword}`,
                      summary: alert.summary,
                      source: alert.source,
                      url: alert.source,
                      date: alert.date,
                      severity: alert.severity,
                      keyword: alert.keyword,
                      isRiskAlert: true
                    }));
                    sectionTitle = "Risk Alerts";
                    sectionIcon = <AlertTriangle className="w-5 h-5 text-[#FF9500]" />;
                  }
                } else if (newsFilter === 'legal_filings') {
                  // Show legal filings if available, otherwise filter news
                  if (webSearchResults.legal_filings?.length > 0) {
                    articlesToShow = webSearchResults.legal_filings.map(filing => ({
                      title: filing.title || `Legal Filing: ${filing.type}`,
                      summary: filing.summary || filing.description,
                      source: filing.source,
                      url: filing.url || filing.source,
                      date: filing.date,
                      isLegalFiling: true
                    }));
                  } else {
                    // Fallback: filter news articles for legal keywords
                    const legalKeywords = ['lawsuit', 'legal', 'court', 'filing', 'settlement', 'litigation', 'attorney', 'plaintiff', 'defendant', 'judge', 'verdict'];
                    articlesToShow = (webSearchResults.news_mentions || []).filter(mention => 
                      legalKeywords.some(keyword => 
                        mention.title?.toLowerCase().includes(keyword) ||
                        mention.summary?.toLowerCase().includes(keyword)
                      )
                    );
                  }
                  sectionTitle = "Legal Filings";
                  sectionIcon = <Scale className="w-5 h-5 text-[#FF3B30]" />;
                } else {
                  // Show all news mentions
                  articlesToShow = webSearchResults.news_mentions || [];
                }
                
                return articlesToShow.length > 0 ? (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                        {sectionIcon}
                        {sectionTitle}
                        {newsFilter !== 'all' && (
                          <Badge variant="secondary" className="ml-2">
                            {articlesToShow.length} {newsFilter === 'risk_alerts' ? 'alerts' : newsFilter === 'legal_filings' ? 'filings' : 'articles'}
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {articlesToShow.map((item, index) => {
                          const isRisk = item.isRiskAlert || item.severity;
                          const isLegal = item.isLegalFiling;
                          
                          return (
                            <div key={`news-${item.url || item.id || item.headline || index}`} className={`p-4 rounded-lg transition-colors ${
                              isRisk 
                                ? 'bg-[#FF9500]/10 border border-[#FF9500]/30 hover:bg-[#FF9500]/20' 
                                : isLegal
                                  ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20'
                                  : 'bg-secondary/30 hover:bg-secondary/50'
                            }`}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium">{item.title}</h4>
                                    {isRisk && (
                                      <Badge className={`text-xs ${
                                        item.severity === 'high' 
                                          ? 'bg-[#FF3B30]/20 text-[#FF3B30]' 
                                          : 'bg-[#FF9500]/20 text-[#FF9500]'
                                      }`}>
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        {item.severity === 'high' ? 'High Risk' : 'Medium Risk'}: {item.keyword}
                                      </Badge>
                                    )}
                                    {isLegal && (
                                      <Badge className="bg-[#FF3B30]/20 text-[#FF3B30] text-xs">
                                        <Scale className="w-3 h-3 mr-1" />
                                        Legal
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.summary}</p>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                    {item.source && <span>{typeof item.source === 'string' && item.source.includes('http') ? new URL(item.source).hostname : item.source}</span>}
                                    {item.date && <span>{new Date(item.date).toLocaleDateString()}</span>}
                                    {isRisk && <span className="text-[#FF9500]">⚠️ {item.action_required || 'Review recommended'}</span>}
                                  </div>
                                </div>
                                {item.url && (
                                  <Button variant="ghost" size="sm" asChild>
                                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ) : newsFilter !== 'all' ? (
                  <Card className="bg-card border-border">
                    <CardContent className="py-8 text-center">
                      <p className="text-muted-foreground">
                        No {newsFilter === 'risk_alerts' ? 'risk alerts' : 'legal filings'} found.
                      </p>
                      <Button 
                        variant="link" 
                        onClick={() => setNewsFilter('all')}
                        className="mt-2"
                      >
                        Show all news
                      </Button>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              {/* Risk Alerts Summary - Only show when not filtered */}
              {newsFilter === 'all' && webSearchResults.risk_alerts?.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
                      Risk Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {webSearchResults.risk_alerts.map((alert, index) => {
                        const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                        return (
                          <Alert key={`alert-${alert.person || alert.summary || index}`} className={config.color}>
                            <config.icon className="h-4 w-4" />
                            <AlertDescription>
                              <strong>{alert.person}</strong>: {alert.summary}
                              <p className="text-xs mt-1">{alert.action_required}</p>
                            </AlertDescription>
                          </Alert>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold">No Search Results</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Scan Web" to search for news and mentions about this organization.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* 990 Tax Data Tab */}
        <TabsContent value="990" className="space-y-6">
          {/* 990 Info */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-start gap-4">
              <FileText className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">IRS Form 990 Data</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Access public 990 tax form data for nonprofit organizations from the ProPublica Nonprofit Explorer. 
                  Includes revenue, expenses, assets, officer compensation, and more.
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <Button
                    onClick={handleFetch990Data}
                    disabled={loadingTax || !effectiveOrgId}
                    size="sm"
                  >
                    {loadingTax ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Fetch 990 Data
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowEinDialog(true)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Set EIN
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {taxData ? (
            <>
              {/* Financial Summary */}
              {taxData.financial_summary && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Financial Summary (FY {taxData.financial_summary.fiscal_year})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Total Revenue</p>
                        <p className="text-xl font-bold font-['Barlow_Condensed'] text-[#34C759]">
                          {formatCurrency(taxData.financial_summary.total_revenue)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Total Expenses</p>
                        <p className="text-xl font-bold font-['Barlow_Condensed'] text-[#FF3B30]">
                          {formatCurrency(taxData.financial_summary.total_expenses)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Net Assets</p>
                        <p className="text-xl font-bold font-['Barlow_Condensed']">
                          {formatCurrency(taxData.financial_summary.net_assets)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Officer Compensation</p>
                        <p className="text-xl font-bold font-['Barlow_Condensed']">
                          {formatCurrency(taxData.financial_summary.total_compensation)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Employees</p>
                        <p className="text-lg font-bold">{taxData.financial_summary.num_employees || 'N/A'}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground uppercase">Volunteers</p>
                        <p className="text-lg font-bold">{taxData.financial_summary.num_volunteers || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tax Records History */}
              {taxData.tax_records?.length > 0 && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Tax Filing History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead>Tax Year</TableHead>
                          <TableHead>Revenue</TableHead>
                          <TableHead>Expenses</TableHead>
                          <TableHead>Assets</TableHead>
                          <TableHead>Liabilities</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {taxData.tax_records.map((record) => (
                          <TableRow key={`tax-${record.tax_period || record.ein || record.id}`} className="border-border">
                            <TableCell className="font-semibold">{record.tax_period}</TableCell>
                            <TableCell className="text-[#34C759]">{formatCurrency(record.total_revenue)}</TableCell>
                            <TableCell className="text-[#FF3B30]">{formatCurrency(record.total_expenses)}</TableCell>
                            <TableCell>{formatCurrency(record.total_assets)}</TableCell>
                            <TableCell>{formatCurrency(record.total_liabilities)}</TableCell>
                            <TableCell>
                              {record.pdf_url && (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={record.pdf_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Potential Matches */}
              {taxData.potential_matches?.length > 0 && !taxData.financial_summary && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                      Potential Nonprofit Matches
                    </CardTitle>
                    <CardDescription>
                      These organizations were found in the nonprofit database. Select the correct one and update your EIN.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {taxData.potential_matches.map((match) => (
                        <div key={`match-${match.ein || match.name}`} className="p-4 rounded-lg bg-secondary/30 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{match.name}</p>
                            <p className="text-sm text-muted-foreground">
                              EIN: {match.ein} • {match.city}, {match.state}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEinInput(match.ein);
                              setIsNonprofit(true);
                              setShowEinDialog(true);
                            }}
                          >
                            Use This EIN
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No Data Message */}
              {taxData.message && !taxData.financial_summary && !taxData.potential_matches?.length && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>{taxData.message}</AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold">No 990 Data</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {org?.ein 
                    ? 'Click "Fetch 990 Data" to retrieve tax form information.'
                    : 'Set an EIN to fetch 990 tax form data for this nonprofit organization.'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary" />
                Organization Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map((alert) => {
                    const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                    const Icon = config.icon;
                    return (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border ${config.color} ${alert.is_read ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">{alert.title}</h4>
                              <Badge variant="outline" className="text-xs capitalize">
                                {alert.type?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(alert.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No alerts for this organization</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* EIN Dialog */}
      <Dialog open={showEinDialog} onOpenChange={setShowEinDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
              Nonprofit Information
            </DialogTitle>
            <DialogDescription>
              Set the EIN (Employer Identification Number) to fetch 990 tax data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <Label>Is this a nonprofit organization?</Label>
              <Switch
                checked={isNonprofit}
                onCheckedChange={setIsNonprofit}
              />
            </div>
            {isNonprofit && (
              <div className="space-y-2">
                <Label>EIN (Employer Identification Number)</Label>
                <Input
                  value={einInput}
                  onChange={(e) => setEinInput(e.target.value)}
                  placeholder="XX-XXXXXXX"
                  className="bg-secondary/50"
                />
                <p className="text-xs text-muted-foreground">
                  Format: XX-XXXXXXX (9 digits with hyphen after first 2)
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEinDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateNonprofitInfo} className="btn-primary-glow">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Populate Preview Dialog */}
      <Dialog open={showAutoPopulatePreview} onOpenChange={(open) => { if (!open) setShowAutoPopulatePreview(false); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              Auto-Populate Preview
            </DialogTitle>
            <DialogDescription>
              Review discovered data before applying to {autoPopulateData?.organization_name}. Select what to update.
            </DialogDescription>
          </DialogHeader>

          {autoPopulateData && (
            <div className="space-y-5 mt-3" data-testid="auto-populate-preview">
              {/* Mission Statement */}
              {autoPopulateData.discovered?.mission_statement && (
                <div className="border rounded-lg p-4 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedChanges.mission_statement}
                      onChange={() => toggleChange('mission_statement')}
                      className="rounded"
                      data-testid="toggle-mission"
                    />
                    <span className="font-semibold text-sm">Mission Statement</span>
                    {autoPopulateData.existing_data?.mission_statement && (
                      <Badge variant="outline" className="text-[10px]">Will overwrite</Badge>
                    )}
                  </label>
                  <p className="text-sm text-muted-foreground ml-6">{autoPopulateData.discovered.mission_statement}</p>
                </div>
              )}

              {/* Contact Info */}
              {autoPopulateData.discovered?.contact_info && Object.values(autoPopulateData.discovered.contact_info).some(v => v) && (
                <div className="border rounded-lg p-4 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedChanges.contact_info}
                      onChange={() => toggleChange('contact_info')}
                      className="rounded"
                      data-testid="toggle-contact"
                    />
                    <span className="font-semibold text-sm">Contact Info</span>
                  </label>
                  <div className="ml-6 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    {Object.entries(autoPopulateData.discovered.contact_info).map(([k, v]) => v ? (
                      <div key={k}><span className="capitalize font-medium">{k}:</span> {v}</div>
                    ) : null)}
                  </div>
                </div>
              )}

              {/* Social Media */}
              {autoPopulateData.discovered?.social_media && Object.values(autoPopulateData.discovered.social_media).some(v => v) && (
                <div className="border rounded-lg p-4 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedChanges.social_media}
                      onChange={() => toggleChange('social_media')}
                      className="rounded"
                      data-testid="toggle-social"
                    />
                    <span className="font-semibold text-sm">Social Media Links</span>
                  </label>
                  <div className="ml-6 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    {Object.entries(autoPopulateData.discovered.social_media).map(([k, v]) => v ? (
                      <div key={k} className="flex items-center gap-1.5">
                        <Globe className="w-3 h-3" />
                        <span className="capitalize font-medium">{k}:</span>
                        <a href={v} target="_blank" rel="noreferrer" className="text-primary truncate max-w-[180px]">{v}</a>
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}

              {/* Organization Size */}
              {autoPopulateData.discovered?.org_size && (
                <div className="border rounded-lg p-4 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedChanges.org_size}
                      onChange={() => toggleChange('org_size')}
                      className="rounded"
                      data-testid="toggle-orgsize"
                    />
                    <span className="font-semibold text-sm">Organization Size</span>
                  </label>
                  <div className="ml-6 flex flex-wrap gap-3 text-sm text-muted-foreground">
                    {autoPopulateData.discovered.org_size.num_employees && <span>Employees: {autoPopulateData.discovered.org_size.num_employees}</span>}
                    {autoPopulateData.discovered.org_size.num_volunteers && <span>Volunteers: {autoPopulateData.discovered.org_size.num_volunteers}</span>}
                    {autoPopulateData.discovered.org_size.num_members && <span>Members: {autoPopulateData.discovered.org_size.num_members}</span>}
                    {autoPopulateData.discovered.org_size.description && <span>{autoPopulateData.discovered.org_size.description}</span>}
                  </div>
                </div>
              )}

              {/* EIN */}
              {autoPopulateData.discovered?.ein && !autoPopulateData.existing_data?.ein && (
                <div className="border rounded-lg p-4 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedChanges.ein}
                      onChange={() => toggleChange('ein')}
                      className="rounded"
                      data-testid="toggle-ein"
                    />
                    <span className="font-semibold text-sm">EIN: {autoPopulateData.discovered.ein}</span>
                    {autoPopulateData.discovered.is_nonprofit && <Badge className="bg-green-500/20 text-green-500 text-[10px]">501(c) Nonprofit</Badge>}
                  </label>
                </div>
              )}

              {/* Leadership */}
              {autoPopulateData.discovered?.leadership?.filter(p => p.is_new).length > 0 && (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    Leadership ({autoPopulateData.discovered.leadership.filter(p => p.is_new).length} new)
                  </div>
                  {autoPopulateData.discovered.leadership.filter(p => p.is_new).map((person, idx) => (
                    <label key={`lead-${person.name || person.full_name || idx}`} className="flex items-center gap-2 ml-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(selectedChanges.leadership || []).includes(idx)}
                        onChange={() => togglePersonIndex('leadership', idx)}
                        className="rounded"
                      />
                      <span className="text-sm">{person.name}</span>
                      <span className="text-xs text-muted-foreground">— {person.title || person.role}</span>
                      {person.source && <Badge variant="outline" className="text-[10px]">{person.source}</Badge>}
                    </label>
                  ))}
                </div>
              )}

              {/* Board Members */}
              {autoPopulateData.discovered?.board_members?.filter(p => p.is_new).length > 0 && (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Board Members ({autoPopulateData.discovered.board_members.filter(p => p.is_new).length} new)
                  </div>
                  {autoPopulateData.discovered.board_members.filter(p => p.is_new).map((person, idx) => (
                    <label key={`board-${person.name || person.full_name || idx}`} className="flex items-center gap-2 ml-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(selectedChanges.board_members || []).includes(idx)}
                        onChange={() => togglePersonIndex('board_members', idx)}
                        className="rounded"
                      />
                      <span className="text-sm">{person.name}</span>
                      <span className="text-xs text-muted-foreground">— {person.role || 'Board Member'}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Staff Members */}
              {autoPopulateData.discovered?.staff_members?.filter(p => p.is_new).length > 0 && (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Staff Members ({autoPopulateData.discovered.staff_members.filter(p => p.is_new).length} new)
                  </div>
                  {autoPopulateData.discovered.staff_members.filter(p => p.is_new).map((person, idx) => (
                    <label key={`staff-${person.name || person.full_name || idx}`} className="flex items-center gap-2 ml-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(selectedChanges.staff_members || []).includes(idx)}
                        onChange={() => togglePersonIndex('staff_members', idx)}
                        className="rounded"
                      />
                      <span className="text-sm">{person.name}</span>
                      <span className="text-xs text-muted-foreground">— {person.role || 'Staff'}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Sources */}
              {autoPopulateData.sources?.length > 0 && (
                <div className="border rounded-lg p-3 bg-muted/30">
                  <p className="text-xs font-semibold text-muted-foreground mb-1.5">Data Sources</p>
                  {autoPopulateData.sources.map((s) => (
                    <div key={`src-${s.url || s.type}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[9px] px-1">{s.type}</Badge>
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-primary truncate max-w-[400px]">{s.url}</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAutoPopulatePreview(false)}>Cancel</Button>
            <Button onClick={handleApplyChanges} disabled={applyingChanges} data-testid="apply-changes-btn">
              {applyingChanges ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Apply Selected Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
